import React, { useEffect, useRef, useState } from 'react';
import { Collapse, DatePicker, Input, InputNumber, Select, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Dayjs } from 'dayjs';
import type { UserOptionDto } from '../../../../dto/reference';
import {
  fetchAccountManagerOptions,
  fetchNotificationRecipientOptions,
} from '../../../reference-data/api/referenceDataApi';
import { useSupplierOptions } from '../../../reference-data/queries/useSupplierOptions';
import { isOrderDraftPrepaymentPercentValid, type OrderDraftHeader } from './orderDraftModel';
import { DEFAULT_PREPAYMENT_PERCENT, ORDER_PAYMENT_MODES } from '../../orderPaymentMode';
import {
  getOrderDraftMoreSectionForProblem,
  isOrderDraftMoreFieldProblem,
  getOrderDraftMoreSectionSummaries,
  getOrderDraftMoreSections,
  getOrderDraftMoreSummary,
  type OrderDraftMoreSectionId,
  type OrderDraftMoreSummaryToken,
} from './orderDraftMoreSummary';
import type { OrderDraftEditorController } from './useOrderDraftEditor';

/** Who may change which header field (backend `orderPermissions.js`). */
export interface OrderDraftMoreDataRights {
  /** Numbering, delivery address, supplier and loading place. */
  commercial: boolean;
  orderDate?: boolean;
  accountManager: boolean;
  customerPayments: boolean;
  /** Our delivery / customer self-pickup. */
  deliveryMode: boolean;
}

interface MoreDataProps {
  editor: OrderDraftEditorController;
  isNew: boolean;
  rights: OrderDraftMoreDataRights;
}

/** Option lists, loaded only when their section is opened (or holds a value that needs a name). */
interface MoreDataOptions {
  accountManagers: UserOptionDto[];
  recipients: UserOptionDto[];
  suppliers: Array<{ id: string; name: string; searchText?: string }>;
}

const SECTION_TITLES: Record<OrderDraftMoreSectionId, string> = {
  numbering: 'orders:process.draft.more.groupNumbering',
  payment: 'orders:process.draft.more.groupPayment',
  supplier: 'orders:process.draft.more.groupSupplier',
  notify: 'orders:process.draft.more.groupNotify',
};

const toKeys = (keys: string | string[]) => (Array.isArray(keys) ? keys : [keys]);

/** Fetches once, the first time `enabled` is true. */
function useLazyList<T>(enabled: boolean, load: () => Promise<T[]>): T[] {
  const [items, setItems] = useState<T[]>([]);
  const loadRef = useRef(load);
  loadRef.current = load;
  const startedRef = useRef(false);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);
  useEffect(() => {
    if (!enabled || startedRef.current) return;
    startedRef.current = true;
    loadRef.current()
      .then((next) => { if (aliveRef.current) setItems(next); })
      .catch(() => { startedRef.current = false; });
  }, [enabled]);
  return items;
}

/**
 * „Więcej danych” (collapsed by default, a quiet add-on of the Szkic step —
 * the mockup shows only customer, address and lines). Every field of the
 * retired create wizard that the mockup does not show stays here, on three
 * levels: the collapsed header lists the values already set as small markers;
 * opened, it shows the few common fields (customer's order number, delivery,
 * dates, notes) and a second collapsed level „Zaawansowane”, which is a short
 * list of section headers (numbering and account manager, payment agreements,
 * supplier and loading place, offer flag and notification recipients, and per
 * line the currency and unit conversion), each its own collapsed panel with a
 * one-line value summary. A section the role has no field in is not rendered;
 * its lists load when it is opened; a refused save (e.g. manual numbering
 * without a number) opens the section with the problem. Which fields exist,
 * who may edit them and what is saved did not change.
 */
export default function OrderDraftMoreData(props: MoreDataProps) {
  const { editor, isNew, rights } = props;
  const { t } = useTranslation(['orders', 'common']);
  const [moreOpen, setMoreOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [openSections, setOpenSections] = useState<string[]>([]);
  const { header } = editor.draft;
  const context = { isNew, ...rights };
  const sections = getOrderDraftMoreSections(context);
  const summary = getOrderDraftMoreSummary(editor.draft, context);

  // A refused save opens the section that holds the field the problem is about.
  const invalidSave = editor.invalidSave;
  useEffect(() => {
    if (isOrderDraftMoreFieldProblem(invalidSave?.problemKey)) {
      setMoreOpen(true);
      return;
    }
    const section = getOrderDraftMoreSectionForProblem(invalidSave?.problemKey);
    if (!section) return;
    setMoreOpen(true);
    setAdvancedOpen(true);
    setOpenSections((current) => (current.includes(section) ? current : [...current, section]));
    // Only a new refusal (its counter) opens a section — not later edits of the draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidSave?.seq]);

  const visible = moreOpen && advancedOpen;
  const isOpen = (id: OrderDraftMoreSectionId) => visible && openSections.includes(id);
  const accountManagers = useLazyList<UserOptionDto>(
    rights.accountManager && visible && (isOpen('numbering') || Boolean(header.accountManagerId)),
    () => fetchAccountManagerOptions('orders.draft.accountManagers'),
  );
  const recipients = useLazyList<UserOptionDto>(
    isNew && isOpen('notify'),
    () => fetchNotificationRecipientOptions('orders.draft.notificationRecipients'),
  );
  const { options: suppliers } = useSupplierOptions(rights.commercial && visible && (isOpen('supplier') || Boolean(header.supplierId)));
  const options: MoreDataOptions = { accountManagers, recipients, suppliers };

  const sectionSummaries = getOrderDraftMoreSectionSummaries(editor.draft, {
    ...context,
    accountManagerName: accountManagers.find((user) => user.id === header.accountManagerId)?.name,
    supplierName: suppliers.find((supplier) => supplier.id === header.supplierId)?.name,
  });

  return (
    <Collapse
      className="zk-draft__more"
      ghost
      destroyOnHidden
      activeKey={moreOpen ? ['more'] : []}
      onChange={(keys) => setMoreOpen(toKeys(keys).length > 0)}
      items={[{
        key: 'more',
        label: (
          <span className="zk-draft__more-label">
            <strong>{t('orders:process.draft.more.title')}</strong>
            {summary.length > 0 ? (
              <span className="zk-draft__more-summary" data-testid="zk-draft-more-summary">
                {summary.map((token) => (
                  <span className="zk-draft__more-tag" key={token.id} data-summary={token.id}>{t(token.key, token.params)}</span>
                ))}
              </span>
            ) : (
              <span className="zk-draft__more-hint">{t('orders:process.draft.more.hint')}</span>
            )}
          </span>
        ),
        children: (
          <MoreDataFields
            {...props}
            options={options}
            sections={sections}
            sectionSummaries={sectionSummaries}
            advancedOpen={advancedOpen}
            onAdvancedChange={setAdvancedOpen}
            openSections={openSections}
            onSectionsChange={setOpenSections}
          />
        ),
      }]}
      data-testid="zk-draft-more"
    />
  );
}

function Field({ label, children, wide = false }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={`zk-draft__field${wide ? ' zk-draft__field--wide' : ''}`}>
      <span className="zk-draft__label">{label}</span>
      {children}
    </div>
  );
}

function DateField({ value, onChange, disabled = false, allowClear = true }: { value: Dayjs | null; onChange: (next: Dayjs | null) => void; disabled?: boolean; allowClear?: boolean }) {
  return <DatePicker value={value} onChange={onChange} disabled={disabled} allowClear={allowClear} format="DD.MM.YYYY" className="zk-draft__control" />;
}

interface MoreDataFieldsProps extends MoreDataProps {
  options: MoreDataOptions;
  sections: OrderDraftMoreSectionId[];
  sectionSummaries: Record<OrderDraftMoreSectionId, OrderDraftMoreSummaryToken[]>;
  advancedOpen: boolean;
  onAdvancedChange: (open: boolean) => void;
  openSections: string[];
  onSectionsChange: (keys: string[]) => void;
}

function MoreDataFields(props: MoreDataFieldsProps) {
  const { editor, rights, isNew, advancedOpen, onAdvancedChange } = props;
  const { t } = useTranslation(['orders', 'common']);
  const { header } = editor.draft;
  const set = (patch: Partial<OrderDraftHeader>) => editor.setHeader(patch);

  return (
    <div className="zk-draft__more-body" data-testid="zk-draft-more-body">
      <div className="zk-draft__fields">
        <Field label={t('orders:modal.clientOrderNumber')}>
          <Input
            value={header.clientOrderNumber}
            placeholder={t('orders:modal.clientOrderNumberPlaceholder')}
            onChange={(event) => set({ clientOrderNumber: event.target.value })}
          />
        </Field>
        {rights.deliveryMode && (
          <Field label={t('orders:deliveryMode.label')}>
            <Select
              value={header.deliveryMode}
              options={(['OUR_DELIVERY', 'CUSTOMER_PICKUP'] as const).map((mode) => ({ value: mode, label: t(`orders:deliveryMode.options.${mode}`) }))}
              onChange={(deliveryMode) => set({ deliveryMode })}
              aria-label={t('orders:deliveryMode.label')}
            />
          </Field>
        )}
        {(isNew || rights.commercial) && (
          <Field label={t('orders:paymentMode.label')}>
            <Select
              value={header.paymentMode}
              options={ORDER_PAYMENT_MODES.map((mode) => ({ value: mode, label: t(`orders:paymentMode.options.${mode}`) }))}
              onChange={(paymentMode) => set({
                paymentMode,
                prepaymentPercent: paymentMode === 'PREPAYMENT' ? (header.prepaymentPercent ?? DEFAULT_PREPAYMENT_PERCENT) : null,
              })}
              aria-label={t('orders:paymentMode.label')}
              data-testid="zk-draft-payment-mode"
            />
          </Field>
        )}
        {(isNew || rights.commercial) && header.paymentMode === 'PREPAYMENT' && (
          <Field label={t('orders:paymentMode.prepaymentPercent')}>
            <InputNumber
              value={header.prepaymentPercent}
              step={5}
              status={isOrderDraftPrepaymentPercentValid(header) ? undefined : 'error'}
              addonAfter="%"
              decimalSeparator=","
              className="zk-draft__control"
              onChange={(value) => set({ prepaymentPercent: typeof value === 'number' ? value : null })}
              aria-label={t('orders:paymentMode.prepaymentPercent')}
              data-testid="zk-draft-prepayment-percent"
            />
            {!isOrderDraftPrepaymentPercentValid(header) && (
              <span className="zk-draft__field-error" role="alert">{t('orders:process.draft.errors.prepaymentPercent')}</span>
            )}
          </Field>
        )}
        <Field label={t('orders:expectedDelivery')}>
          <DateField value={header.expectedDelivery} onChange={(expectedDelivery) => set({ expectedDelivery })} />
        </Field>
        {rights.customerPayments && (
          <Field label={t('orders:modal.customerPaymentDueDate')}>
            <DateField value={header.customerPaymentDueDate} onChange={(customerPaymentDueDate) => set({ customerPaymentDueDate })} />
          </Field>
        )}
        <Field label={t('orders:notes')} wide>
          <Input.TextArea rows={2} value={header.notes} onChange={(event) => set({ notes: event.target.value })} />
        </Field>
      </div>

      <Collapse
        className="zk-draft__advanced"
        ghost
        size="small"
        destroyOnHidden
        activeKey={advancedOpen ? ['advanced'] : []}
        onChange={(keys) => onAdvancedChange(toKeys(keys).length > 0)}
        items={[{
          key: 'advanced',
          label: <span className="zk-draft__advanced-label">{t('orders:process.draft.more.advanced')}</span>,
          children: <AdvancedSections {...props} />,
        }]}
        data-testid="zk-draft-advanced"
      />
    </div>
  );
}

/** Independent collapsed panels, one per section; the fields (and their lists) mount when a panel opens. */
function AdvancedSections(props: MoreDataFieldsProps) {
  const { sections, sectionSummaries, openSections, onSectionsChange } = props;
  const { t } = useTranslation(['orders', 'common']);
  return (
    <div className="zk-draft__advanced-body" data-testid="zk-draft-advanced-body">
      <Collapse
        className="zk-draft__sections"
        ghost
        size="small"
        destroyOnHidden
        activeKey={openSections}
        onChange={(keys) => onSectionsChange(toKeys(keys))}
        items={sections.map((id) => {
          const tokens = sectionSummaries[id];
          return {
            key: id,
            label: (
              <span className="zk-draft__section-label" data-testid={`zk-draft-section-${id}-header`}>
                <span className="zk-draft__section-title">{t(SECTION_TITLES[id])}</span>
                {tokens.length > 0 && (
                  <span className="zk-draft__section-summary" data-testid={`zk-draft-section-${id}-summary`}>
                    {tokens.map((token) => t(token.key, token.params)).join(' · ')}
                  </span>
                )}
              </span>
            ),
            children: <div className="zk-draft__section-body" data-testid={`zk-draft-section-${id}`}><SectionFields id={id} {...props} /></div>,
          };
        })}
      />
    </div>
  );
}

function SectionFields({ id, editor, rights, options, isNew }: MoreDataFieldsProps & { id: OrderDraftMoreSectionId }) {
  const { t } = useTranslation(['orders', 'common']);
  const { header } = editor.draft;
  const set = (patch: Partial<OrderDraftHeader>) => editor.setHeader(patch);
  const userLabel = (user: UserOptionDto) => (user.email ? `${user.name} (${user.email})` : user.name);

  if (id === 'numbering') {
    return (
      <div className="zk-draft__fields">
        <Field label={t('orders:modal.orderDate')}>
          <DateField allowClear={false} value={header.orderDate} onChange={(orderDate) => set({ orderDate })} disabled={!isNew && !rights.orderDate} />
        </Field>
        {rights.accountManager && (
          <Field label={t('orders:modal.accountManager')}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              value={header.accountManagerId || undefined}
              placeholder={t('orders:modal.selectAccountManager')}
              options={options.accountManagers.map((user) => ({ value: user.id, label: userLabel(user) }))}
              onChange={(accountManagerId) => set({ accountManagerId: accountManagerId || null })}
            />
          </Field>
        )}
        <Field label={t('orders:modal.numberingMode')}>
          <span className="zk-draft__inline">
            <Switch
              checked={header.useManualNumbering}
              disabled={!rights.commercial}
              checkedChildren={t('orders:modal.manualNumberingShort')}
              unCheckedChildren={t('orders:modal.automaticNumberingShort')}
              onChange={(useManualNumbering) => set({ useManualNumbering, ...(useManualNumbering ? {} : { manualOrderNumber: '' }) })}
              aria-label={t('orders:modal.numberingMode')}
            />
          </span>
        </Field>
        {header.useManualNumbering && (
          <Field label={t('orders:modal.manualOrderNumber')}>
            <Input
              maxLength={64}
              value={header.manualOrderNumber}
              disabled={!rights.commercial}
              placeholder={t('orders:modal.manualOrderNumberPlaceholder')}
              onChange={(event) => set({ manualOrderNumber: event.target.value })}
            />
          </Field>
        )}
      </div>
    );
  }

  if (id === 'payment') {
    return (
      <div className="zk-draft__fields">
        <Field label={t('orders:modal.customerPaymentNotes')} wide>
          <Input
            value={header.customerPaymentNotes}
            placeholder={t('orders:modal.customerPaymentNotesPlaceholder')}
            onChange={(event) => set({ customerPaymentNotes: event.target.value })}
          />
        </Field>
      </div>
    );
  }

  if (id === 'supplier') {
    return (
      <div className="zk-draft__fields">
        <Field label={t('orders:modal.supplier')}>
          <Select
            allowClear
            showSearch
            optionFilterProp="searchText"
            value={header.supplierId || undefined}
            placeholder={t('orders:modal.selectSupplier')}
            options={options.suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name, searchText: supplier.searchText }))}
            onChange={(supplierId) => set({ supplierId: supplierId || null })}
          />
        </Field>
        <Field label={t('orders:modal.factoryLocation')}>
          <Input
            value={header.factoryLocation}
            placeholder={t('orders:modal.factoryLocationPlaceholder')}
            onChange={(event) => set({ factoryLocation: event.target.value })}
          />
        </Field>
      </div>
    );
  }

  // notify (only a new order: the update has no recipients field)
  return (
    <div className="zk-draft__fields">
      <Field label={t('orders:modal.notifyUsers')}>
        <Select
          mode="multiple"
          allowClear
          optionFilterProp="label"
          value={header.notifyUsers}
          placeholder={t('orders:modal.selectUsersToNotify')}
          options={options.recipients.map((user) => ({ value: user.id, label: userLabel(user) }))}
          onChange={(notifyUsers) => set({ notifyUsers })}
        />
      </Field>
    </div>
  );
}
